import { FC, useState, useEffect } from "react";
import { DynamicListWidgetConfig, SOURCES_COLUMNS, WORKSPACE_DOCUMENT_COLUMNS } from "@/models/dashboard/WidgetConfig";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Skeleton,
  Button,
  Badge,
} from "@capacit-com/lara-law-shared-ui";
import { useWidgetData, WidgetDataItem } from "@/hooks/useWidgetData";
import { useDebounce } from "@/hooks/useDebounce";
import { AlertCircle, RefreshCw, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { useOpenAppNavigation } from "@/hooks/useOpenAppNavigation";
import { resolveVisibleWidgetColumns } from "../lib/widgetColumns";
import {
  legislationTarget,
  NEW_TAB_MODIFIER_TOOLTIP,
  workspaceDocumentTarget,
} from "@/lib/navigation/appNavigation";
import SourceFavoriteCell, {
  SourceFavoriteHead,
} from "../components/SourceFavoriteCell";
import { useFavoriteSourceIds } from "../hooks/useFavoriteSourceIds";
import {
  isSourceFavorited,
  resolveDynamicListSourceTarget,
} from "../lib/favoriteSourceRows";

interface DynamicListWidgetProps {
  widgetId: string;
  name: string;
  config: DynamicListWidgetConfig;
}

const DynamicListWidget: FC<DynamicListWidgetProps> = ({
  widgetId: _widgetId,
  name: _name,
  config,
}) => {
  const { openNavigation } = useOpenAppNavigation();
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const favoriteSourceIds = useFavoriteSourceIds();

  // Reset to page 1 when config changes
  useEffect(() => {
    setCurrentPage(1);
  }, [config.dataSource, config.pageSize, config.sortBy, config.sortAscending, config.searchQuery, config.filters]);

  // The editor panel patches the search term on every keystroke (LAR-726); wait for a pause
  // before fetching, and show the loading state meanwhile so the old rows never look final.
  const debouncedSearchQuery = useDebounce(config.searchQuery, 300);
  const queryConfig = { ...config, searchQuery: debouncedSearchQuery };
  const { data, isLoading, isFetching: isFetchingRows, isError, refetch } = useWidgetData(queryConfig, { pageNumber: currentPage });
  const isFetching = isFetchingRows || debouncedSearchQuery !== config.searchQuery;

  const columns = config.dataSource === "sources" 
    ? SOURCES_COLUMNS 
    : WORKSPACE_DOCUMENT_COLUMNS;

  const visibleColumns = resolveVisibleWidgetColumns(
    columns,
    config.visibleColumns
  );

  const handleRowClick = (
    item: WidgetDataItem,
    event?: React.MouseEvent
  ) => {
    if (config.dataSource === "sources") {
      if (!item.externalId || !item.datasource) {
        return;
      }
      openNavigation(
        legislationTarget(item.externalId, item.datasource),
        event
      );
      return;
    }

    if (
      config.dataSource === "workspace-documents" &&
      item.workspaceId &&
      item.documentId
    ) {
      openNavigation(
        workspaceDocumentTarget(item.workspaceId, item.documentId),
        event
      );
    }
  };

  const showFavoriteColumn = config.dataSource === "sources";

  const isRowClickable = (item: WidgetDataItem) =>
    config.dataSource === "sources" ||
    (config.dataSource === "workspace-documents" &&
      Boolean(item.workspaceId && item.documentId));

  return (
    <div className="h-full flex flex-col">
      {/* Loading state — overflow-hidden clips rows to fill the widget height exactly */}
      {isLoading && (
        <div className="flex-1 overflow-hidden flex flex-col gap-2 p-2">
          <Skeleton className="h-8 w-full shrink-0" />
          {Array.from({ length: 20 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full shrink-0" />
          ))}
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="flex-1 flex flex-col items-center justify-center py-8 space-y-4 text-muted-foreground">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm">Der opstod en fejl ved indlæsning af data</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Prøv igen
          </Button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && (!data || data.items.length === 0) && (
        <div className="flex-1 flex flex-col items-center justify-center py-8 space-y-3 text-muted-foreground">
          <FileText className="h-8 w-8" />
          <p className="text-sm">Ingen resultater fundet</p>
          {Object.keys(config.filters).length > 0 && (
            <p className="text-xs">Prøv at ændre filtrene for at se flere resultater</p>
          )}
        </div>
      )}

      {/* Data */}
      {!isLoading && !isError && data && data.items.length > 0 && (
        <>
          <div className={`themed-scrollbar flex-1 overflow-x-auto overflow-y-auto relative transition-opacity duration-150 ${isFetching ? "opacity-50 pointer-events-none" : ""}`}>
            <Table removeOverflowX={true}>
              <TableHeader className="sticky top-0 z-10 shadow-sm">
                <TableRow>
                  {visibleColumns.map((col) => (
                    <TableHead
                      key={col.id}
                      className={
                        col.id === "title" || col.id === "name"
                          ? "w-full text-xs h-8"
                          : "text-xs h-8"
                      }
                    >
                      {col.label}
                    </TableHead>
                  ))}
                  {showFavoriteColumn && <SourceFavoriteHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((item) => (
                  <TableRow
                    key={item.id}
                    className={
                      isRowClickable(item)
                        ? "cursor-pointer hover:bg-muted/50"
                        : ""
                    }
                    title={isRowClickable(item) ? NEW_TAB_MODIFIER_TOOLTIP : undefined}
                    onClick={(e) => handleRowClick(item, e)}
                    onMouseEnter={() => setHoveredRowId(item.id)}
                    onMouseLeave={() =>
                      setHoveredRowId((current) =>
                        current === item.id ? null : current
                      )
                    }
                  >
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.id}
                        className={
                          col.id === "title" || col.id === "name"
                            ? "py-2 w-full max-w-0 min-w-[100px] overflow-hidden"
                            : "py-2"
                        }
                      >
                        {col.id === "title" && (
                          <span className="text-sm font-medium truncate block" title={item.title ?? undefined}>
                            {item.title}
                          </span>
                        )}
                        {col.id === "name" && (
                          <span className="text-sm font-medium truncate block" title={item.name ?? item.title ?? undefined}>
                            {item.name || item.title}
                          </span>
                        )}
                        {col.id === "type" && (
                          <Badge variant="secondary" className="text-xs">
                            {item.type}
                          </Badge>
                        )}
                        {col.id === "datasource" && (
                          <span className="text-xs text-muted-foreground">{item.datasource}</span>
                        )}
                        {col.id === "status" && (
                          <Badge
                            variant={item.status === "Gældende" ? "default" : "outline"}
                            className="text-xs"
                          >
                            {item.status}
                          </Badge>
                        )}
                        {col.id === "date" && (
                          <span className="text-xs text-muted-foreground">{item.date}</span>
                        )}
                        {col.id === "workspace" && (
                          <span className="text-xs text-muted-foreground">{item.workspace || "-"}</span>
                        )}
                        {col.id === "updatedAt" && (
                          <span className="text-xs text-muted-foreground">{item.updatedAt || "-"}</span>
                        )}
                        {col.id === "createdAt" && (
                          <span className="text-xs text-muted-foreground">{item.createdAt || "-"}</span>
                        )}
                      </TableCell>
                    ))}
                    {showFavoriteColumn && (
                      <SourceFavoriteCell
                        target={resolveDynamicListSourceTarget(
                          item,
                          config.dataSource
                        )}
                        isFavorite={isSourceFavorited(
                          favoriteSourceIds,
                          item.externalId
                        )}
                        isHovering={hoveredRowId === item.id}
                      />
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Page-change loading spinner — overlays the table area only */}
            {isFetching && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <RefreshCw className="h-5 w-5 text-muted-foreground animate-spin" />
              </div>
            )}
          </div>

          {/* Sticky pagination footer */}
          {data.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2 border-t border-border bg-card sticky bottom-0">
              <span className="text-xs text-muted-foreground">
                Side {currentPage} af {data.totalPages}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={currentPage <= 1 || isFetching}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={currentPage >= data.totalPages || isFetching}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DynamicListWidget;
